/**
 * Class for displaying products from Shopify accounts
 *
 * Handles shop page, product detail page, products block and shopping cart rendering.
 * Also toggles the cart icon in the main navigation and mobile navigation if there are any items in the cart.
 */
class Shop {
  constructor() {
    this.config = window.shop || {}
    this.template = document.querySelector("[js-shop-product-template]")

    // loader should be displayed by default, content d-none
    this.loaders = document.querySelectorAll("[js-shop-loader]")
    this.contents = document.querySelectorAll("[js-shop-content]")
    this.productsQuantity = document.querySelectorAll("[js-product-quantity]")
    this.dialog = document.getElementById("product__dialog")

    this.moneyFormat = "${{amount}}" // amount will always be in NZD

    this.menuCartLink = document.querySelector("[js-shop-cart-link]")
    this.menuCartLinkMobile = document.querySelector("[js-shop-cart-link-mobile]")

    if (document.querySelector("[js-shop-page]")) {
      this.productList = document.querySelector("[js-shop-products]")
      this.productsCounts = document.querySelectorAll("[js-shop-count]")

      // TODO: Turn it on and filter on collections that products grouped in a future project / support ticket
      // this.typeDropdown = document.querySelector("[js-product-type]")

      this.sortDropdown = document.querySelector("[js-product-sort]")
      this.initShopPage()
    }

    this.shopContainer = document.querySelector("[js-shop-items-block]")
    if (this.shopContainer) {
      this.singleProducts = document.querySelectorAll("[js-single-product]")
      this.initSingleProducts()
    }

    if (document.querySelector("[js-shop-product]")) {
      this.productDetail = document.querySelector("[js-product-detail]")

      this.initProduct(this.config.product)
    }

    if (this.checkoutDetails) {
      this.initMenuCartLink()
    }

    if (document.querySelector("[js-shop-cart-page]")) {
      this.cart = document.querySelector("[js-shop-cart]")
      this.btnCheckout = document.querySelector("[js-shop-checkout]")
      this.initCart()
    }

    if (this.productsQuantity.length) {
      this.validateMaxQuantity()
    }

    if (this.dialog) {
      this.dialog.addEventListener("click", e => {
        if (e.target.matches("[js-shop-product-close]")) {
          this.dialog.close()
        }
      })
    }
  }

  get client() {
    if (!this._client) {
      /* global ShopifyBuy */
      this._client = ShopifyBuy.buildClient({
        domain: this.config.url,
        storefrontAccessToken: this.config.token,
      })
    }

    return this._client
  }

  /**
   * Checkout details are stored in the local storage
   */
  get checkoutDetails() {
    return JSON.parse(localStorage.getItem("CSNZ-shop-checkout"))
  }

  set checkoutDetails(details) {
    localStorage.setItem("CSNZ-shop-checkout", JSON.stringify(details))
  }

  /**
   * Display desktop and mobile cart link when there is one
   */
  initMenuCartLink() {
    if (this.menuCartLink) {
      this.menuCartLink.parentNode.setAttribute("js-active-cart", true)
      this.menuCartLink.classList.remove("d-none")
    }

    if (this.menuCartLinkMobile) {
      this.menuCartLinkMobile.classList.remove("d-none")
    }
  }

  async initShopPage() {
    this.allProducts = await this.client.product.fetchAll(100)

    this.populateShopProducts()
    // TODO: Turn it on and filter on collections that products grouped in a future project / support ticket
    // this.populateFilters()

    this.setShopPageEvents()
    this.finishLoading()
  }

  async initProduct(productHandle) {
    try {
      this.product = await this.client.product.fetchByHandle(productHandle)
    } catch (e) {
      // redirect to shop page
      // most likely to be error while fetching data of non-existent product
      window.location = window.location.href.substring(0, window.location.href.lastIndexOf("/"))
      return
    }

    const productNode = this.populateProductNode(this.product, "detail")
    this.template.replaceWith(productNode)

    this.setButtonsEvents(productNode)
    this.updatePageDoms(this.product)
    this.finishLoading()
  }

  /**
   * Init single products tiles in the content blocks
   */
  async initSingleProducts() {
    // Products that should be visible
    const products = document.querySelectorAll("[js-single-product]")
    // Number of products processed by fetching data and update frontend
    let processed = 0

    await Promise.all(
      Array.from(products).map(async productNode => {
        const handle = productNode.getAttribute("js-single-product")

        try {
          const product = await this.client.product.fetchByHandle(handle)

          productNode.dataset.productID = product.variants[0].id

          this.populateProductNode(product, "list-item", productNode)
          this.setButtonsEvents(productNode)
          ++processed
        } catch (e) {
          console.error(`Failed fetching data for product ${handle}`)
          productNode.remove()
        }
      })
    )

    // Hide shop block if we are unable to fetch any data
    if (products.length && !processed) {
      this.shopContainer.parentNode.classList.add("d-none")
    }

    this.finishLoading()
  }

  async initCart() {
    // redirect to shop page when no items in cart
    if (!this.checkoutDetails) {
      window.location = this.config.shopUrl
      return
    }

    this.cartItems = await this.getCheckoutItems()
    this.populateCart()

    // final checkout link
    this.btnCheckout.addEventListener("click", e => {
      e.preventDefault()
      this.completeCheckout()
    })

    this.finishLoading()
  }

  finishLoading() {
    this.loaders.forEach(loader => loader.classList.add("d-none"))
    this.contents.forEach(content => content.classList.remove("d-none"))
  }

  setShopPageEvents() {
    // TODO: Turn it on and filter on collections that products grouped in a future project / support ticket
    // this.typeDropdown.addEventListener("change", () => {
    //   this.populateShopProducts()
    // })

    this.sortDropdown.addEventListener("change", () => {
      this.populateShopProducts()
    })
  }

  updatePageDoms(product) {
    const breadcrumb = document.querySelector(".breadcrumb__link--last")

    if (breadcrumb) {
      breadcrumb.innerHTML = breadcrumb.innerHTML.replace("{{productTitle}}", product.title)
      breadcrumb.classList.remove("d-none")
    }
  }

  setButtonsEvents(productNode) {
    const nodeComponents = this.getNodeComponents(productNode)

    const btnAddToCart = productNode.querySelector("[js-shop-add-to-cart]")
    const btnBuyNow = productNode.querySelector("[js-shop-buy-now]")

    const productID = productNode.dataset.productID || this.product.variants[0].id

    if (btnAddToCart) {
      btnAddToCart.addEventListener("click", async e => {
        e.preventDefault()

        const state = btnAddToCart.getAttribute("js-checkout-state")
        const quantity = (nodeComponents.quantity ? parseInt(nodeComponents.quantity.value) : null) || 1

        if (!state) {
          btnAddToCart.innerHTML = this.inlineLoaderHTML("Adding to cart")
          btnAddToCart.setAttribute("js-checkout-state", "adding")

          await this.addToCheckout(productID, quantity)

          btnAddToCart.innerHTML = "See the cart"
          btnAddToCart.setAttribute("js-checkout-state", "added")
          return
        }

        if (state === "added") {
          window.location = this.makeLink(this.checkoutDetails.shopUrl, "cart")
          return
        }
      })
    }

    if (btnBuyNow) {
      btnBuyNow.addEventListener("click", async e => {
        e.preventDefault()

        const state = btnBuyNow.getAttribute("js-checkout-state")
        const quantity = (nodeComponents.quantity ? parseInt(nodeComponents.quantity.value) : null) || 1

        if (!state) {
          btnBuyNow.innerHTML = this.inlineLoaderHTML("Processing")
          btnBuyNow.setAttribute("js-checkout-state", "adding")

          await this.addToCheckout(productID, quantity)
          this.completeCheckout()
        }
      })
    }
  }

  setCartItemEvents(productNode, item) {
    const nodeComponents = this.getNodeComponents(productNode)

    const btnRemove = productNode.querySelector("[js-shop-remove-from-cart]")
    const btnUpdate = productNode.querySelector("[js-shop-update-quantity]")

    btnRemove.addEventListener("click", async e => {
      e.preventDefault()

      const state = btnRemove.getAttribute("js-checkout-state")

      if (!state) {
        btnRemove.innerHTML = this.inlineLoaderHTML("Removing")
        btnRemove.setAttribute("js-checkout-state", "removing")

        await this.removeFromCheckout(item.id)
        this.cartItems = this.cartItems.filter(i => i.id !== item.id)

        if (this.cartItems.length) {
          productNode.remove()
        } else {
          // rerender cart to display the empty message
          this.populateCart()
        }
      }
    })

    btnUpdate.addEventListener("click", async e => {
      e.preventDefault()

      const state = btnUpdate.getAttribute("js-checkout-state")

      if (!state) {
        btnUpdate.innerHTML = this.inlineLoaderHTML("Updating")
        btnUpdate.setAttribute("js-checkout-state", "updating")

        const newQuantity = parseInt(nodeComponents.quantity.value) || 1
        const checkout = await this.updateCheckout(item.id, newQuantity)

        // Update the items
        this.cartItems = checkout.lineItems

        btnUpdate.innerHTML = "Update"
        btnUpdate.classList.add("d-none")
      }
    })

    nodeComponents.quantity.addEventListener("change", e => {
      const oldQuantity = this.cartItems.find(i => i.id === item.id).quantity
      const maxQuantity = parseInt(e.target.getAttribute("max"))
      const newQuantity = parseInt(e.target.value)

      if (newQuantity > maxQuantity) {
        e.target.value = oldQuantity
      } else if (newQuantity < 1) {
        e.target.value = oldQuantity
      }
      btnUpdate.classList.toggle("d-none", oldQuantity === parseInt(e.target.value))
    })
  }

  populateShopProducts() {
    this.productList.innerHTML = ""

    const products = this.getProducts()

    products.forEach(product => {
      const productNode = this.populateProductNode(product, "list-item")
      this.productList.append(productNode)
    })

    this.productsCounts.forEach(
      node => (node.innerText = `${products.length} product${products.length == 1 ? "" : "s"}`)
    )
  }

  populateCart() {
    this.cart.innerHTML = ""

    if (this.cartItems.length) {
      this.cartItems.forEach(item => {
        const productNode = this.populateProductNode(item, "cart")
        this.setCartItemEvents(productNode, item)
        this.cart.append(productNode)
      })
    } else {
      this.btnCheckout.classList.add("d-none")
      this.cart.innerHTML = `<h2 class="h1 my-5 py-5">There are no items in the cart.</h2>`
      this.checkoutDetails = null
    }
  }

  // TODO: Turn it on and filter on collections that products grouped in a future project / support ticket
  /* populateFilters() {
    const productTypes = this.allProducts
      .map(product => product.productType)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map(type => ({ value: type, label: type }))

    window.choices[this.typeDropdown.getAttribute("js-choices-instance")].setChoices(productTypes)
  }*/

  populateProductDetail() {
    const productNode = this.populateProductNode(this.product, "detail")
    this.productDetail.replaceWith(productNode)
    this.productDetail = productNode
  }

  /**
   * Get filtered & sorted products to display on shop page
   */
  getProducts() {
    let products = this.allProducts

    /**
     * TODO: Turn it on and filter on collections that products grouped in a future project / support ticket
    if (this.typeDropdown?.value) {
    products = products.filter(product => product.productType === this.typeDropdown.value)
    }*/

    if (this.sortDropdown?.value) {
      const [sortProp, sortDir] = this.sortDropdown.value.split("_")
      products = products.sort((a, b) => {
        return (sortDir == "asc" ? 1 : -1) * a[sortProp].localeCompare(b[sortProp])
      })
    }

    return products
  }

  /**
   * Populates either product template node or provided productNode with details of the product
   *
   * @param {Object} product
   * @param {string} type link/detail/cart
   * @param {Object} productNode
   */
  populateProductNode(product, type, productNode = null) {
    productNode = productNode || this.template.cloneNode(true)
    const variant = product.variants ? product.variants[0] : product.variant
    let showCompareAtPrice = true

    const components = this.getNodeComponents(productNode)

    if (variant.image) {
      components.image.style.backgroundImage = `url('${variant.image.src}')`

      components.imagePlaceholder.remove()
    } else {
      components.image.remove()
    }

    switch (type) {
      case "list-item":
        components.link.href = this.makeLink(components.link.href, product.handle)
        break
      case "detail":
        components.description.innerHTML = product.descriptionHtml
        break
      case "cart":
        components.quantity.value = product.quantity
        components.link.href = this.makeLink(components.link.href, variant.product.handle)
        components.titleLink.href = this.makeLink(components.titleLink.href, variant.product.handle)
        showCompareAtPrice = false
        break
    }

    // common components
    components.name.innerText = product.title

    if (!variant.compareAtPrice || !showCompareAtPrice) {
      components.price.innerText = this.moneyFormat.replace("{{amount}}", this.formatPrice(variant.price?.amount))
      components.priceOld?.remove()

      if (components.tags) {
        components.tags.remove()
      }
    } else {
      components.price.innerText = this.moneyFormat.replace("{{amount}}", this.formatPrice(variant.price?.amount))
      components.price.classList.add("product__price--discount")
      components.priceOld.innerText = this.moneyFormat.replace(
        "{{amount}}",
        this.formatPrice(variant.compareAtPrice?.amount)
      )
    }

    return productNode
  }

  /**
   * @param {string} baseLink
   * @param {string} segment
   * @returns {string|string}
   */
  makeLink(baseLink, segment) {
    const [path, query] = baseLink.split("?")

    return path + segment + (query ? "?" + query : "")
  }

  /**
   *
   * @param {string} price
   * @returns {string}
   */
  formatPrice(price) {
    if (!price) {
      return "00.00"
    }

    return parseFloat(price).toFixed(2)
  }

  getNodeComponents(productNode) {
    return {
      name: productNode.querySelector("[js-product-name]"),
      price: productNode.querySelector("[js-product-price]"),
      priceOld: productNode.querySelector("[js-product-price-old]"),
      tags: productNode.querySelector("[js-product-tags]"),
      link: productNode.querySelector("[js-product-link]"),
      titleLink: productNode.querySelector("[js-product-title-link]"),
      image: productNode.querySelector("[js-product-image]"),
      imagePlaceholder: productNode.querySelector("[js-product-image-placeholder]"),
      description: productNode.querySelector("[js-product-description]"),
      quantity: productNode.querySelector("[js-product-quantity]"),
    }
  }

  async createOrGetCheckout() {
    if (!this.checkoutDetails || this.checkoutDetails.shopUrl != this.config.shopUrl) {
      const checkout = await this.client.checkout.create()

      this.checkoutDetails = {
        id: checkout.id,
        url: checkout.webUrl,
        shopUrl: this.config.shopUrl,
      }
    }

    return this.checkoutDetails
  }

  async addToCheckout(variantId, quantity) {
    const checkoutDetails = await this.createOrGetCheckout()
    await this.client.checkout.addLineItems(checkoutDetails.id, [{ variantId, quantity }])
    this.initMenuCartLink()
  }

  async removeFromCheckout(lineItemId) {
    return await this.client.checkout.removeLineItems(this.checkoutDetails.id, [lineItemId])
  }

  async updateCheckout(lineItemId, quantity) {
    return await this.client.checkout.updateLineItems(this.checkoutDetails.id, [{ id: lineItemId, quantity: quantity }])
  }

  /**
   * Custom query for retreiving contents of the checkout
   * Standard one does not include the product handle
   *
   * @returns [] items
   */
  async getCheckoutItems() {
    const query = this.client.graphQLClient.query(root => {
      root.add("node", { args: { id: this.checkoutDetails.id } }, node => {
        node.addInlineFragmentOn("Checkout", checkout => {
          checkout.add("id")
          checkout.add("lineItems", { args: { first: 100 } }, items => {
            items.add("pageInfo", pageInfo => {
              // hasNextPage is false even when shouldn't, using first 100 instead
              pageInfo.add("hasNextPage")
              pageInfo.add("hasPreviousPage")
            })
            items.add("edges", edges => {
              edges.add("node", node => {
                node.add("id")
                node.add("title")
                node.add("quantity")
                node.add("variant", variant => {
                  variant.add("product", product => {
                    product.add("handle")
                  })
                  variant.add("image", image => {
                    image.add("url", {
                      alias: "src",
                    })
                  })
                  variant.add("price", price => {
                    price.add("amount")
                  })
                })
              })
            })
          })
        })
      })
    })

    try {
      const { model } = await this.client.graphQLClient.send(query)
      return model.node.lineItems
    } catch (e) {
      console.error(e)
    }

    return []
  }

  completeCheckout() {
    if (this.checkoutDetails) {
      const checkoutUrl = this.checkoutDetails.url
      this.checkoutDetails = null
      window.location = checkoutUrl
    }
  }

  inlineLoaderHTML(text) {
    return `<span class="loading">${text}<span>.</span><span>.</span><span>.</span></span>`
  }

  validateMaxQuantity() {
    document.addEventListener("change", function(e) {
      if (e.target.matches("[js-product-quantity]")) {
        const maxQuantity = parseInt(e.target.getAttribute("max"))
        const newQuantity = parseInt(e.target.value)
        const dialog = document.getElementById("product__dialog")

        if (newQuantity > maxQuantity) {
          e.target.value = maxQuantity

          //show error message
          const error = document.querySelector("[js-shop-product-error]")
          const errorText = document.querySelector("[js-shop-product-error-text]")

          if (error) {
            dialog.showModal()
            error.classList.remove("d-none")
            errorText.innerHTML = `We are only able to process orders of up to ${maxQuantity} units. If you would like to order this product in bulk, please fill out the <a href="/bulk-orders-and-wholesale/bulk-orders-and-wholesale-enquiries/bulk-purchase-order-form/">bulk order form</a>.`
          }
        }
      }
    })
  }
}

export default Shop
